// src/views/SyncAudit/UnmatchedItems.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { Badge, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import DynamicBreadcrumb from "../header/breadcrumb";
import Loader from "../loader/loader";
import syncAuditApi from "../../services/syncAuditApi";

const fmt = (v) => (v ? new Date(v).toLocaleString() : "-");

// datetime-local -> "YYYY-MM-DD HH:mm:ss"
const toApiDateTime = (dtLocal) => {
    if (!dtLocal) return null;
    return dtLocal.replace("T", " ") + ":00";
};

// Normalize response wrapper Common.buildResponse
const extractPayload = (resp) => {
    if (!resp) return null;
    if (resp.rows && typeof resp.total !== "undefined") return resp;
    if (resp.data?.rows && typeof resp.data?.total !== "undefined") return resp.data;
    if (resp.data?.data?.rows && typeof resp.data?.data?.total !== "undefined") return resp.data.data;
    if (resp.data?.data && typeof resp.data?.data === "object") return resp.data.data;
    if (resp.data && typeof resp.data === "object") return resp.data;
    return resp;
};

const normalizeRow = (r) => {
    if (!r) return r;
    return {
        ...r,
        branch_id: r.branch_id ?? r.branchId,
        branch_name: r.branch_name ?? r.branchName,
        item_ref_code: r.item_ref_code ?? r.itemRefCode ?? r.refCode,
        gofrugal_item_id: r.gofrugal_item_id ?? r.gofrugalItemId,
        item_name: r.item_name ?? r.itemName,
        uom: r.uom,
        raw_stock: r.raw_stock ?? r.rawStock,
        raw_mrp: r.raw_mrp ?? r.rawMrp,
        raw_sale_price: r.raw_sale_price ?? r.rawSalePrice,
        item_timestamp: r.item_timestamp ?? r.itemTimestamp,
        first_seen_at: r.first_seen_at ?? r.firstSeenAt,
        last_seen_at: r.last_seen_at ?? r.lastSeenAt,
        seen_count: r.seen_count ?? r.seenCount,
        last_sync_run_id: r.last_sync_run_id ?? r.lastSyncRunId,
        last_sync_branch_row_id: r.last_sync_branch_row_id ?? r.lastSyncBranchRowId,
    };
};

export default function UnmatchedItems() {
    const pathArray = [
        { name: "Reports", url: "" },
        { name: "GoFrugal Sync Audit", url: "/admin/gofrugal-sync" },
        { name: "Unmatched Items", url: "" },
    ];

    const [loading, setLoading] = useState(false);

    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    // Filters (controlled)
    const [branchId, setBranchId] = useState("");
    const [fromLocal, setFromLocal] = useState("");
    const [toLocal, setToLocal] = useState("");
    const [search, setSearch] = useState("");

    // Applied filters
    const [applied, setApplied] = useState({
        branchId: null,
        from: null,
        to: null,
        search: null,
    });

    const searchDebounceRef = useRef(null);

    const columns = useMemo(
        () => [
            { field: "branch_id", headerName: "Branch ID", width: 95 },
            { field: "branch_name", headerName: "Branch", width: 210, renderCell: (p) => p?.row?.branch_name ?? "-" },

            { field: "gofrugal_item_id", headerName: "GF Item ID", width: 120, renderCell: (p) => p?.row?.gofrugal_item_id ?? "-" },

            { field: "item_name", headerName: "Item Name", width: 260, renderCell: (p) => p?.row?.item_name ?? "-" },
            { field: "uom", headerName: "UOM", width: 90, renderCell: (p) => p?.row?.uom ?? "-" },

            {
                field: "raw_stock",
                headerName: "Raw Stock",
                width: 105,
                renderCell: (p) => {
                    const v = p?.row?.raw_stock ?? 0;
                    const n = Number(v);
                    const bg = n === 0 ? "danger" : "secondary";
                    return <Badge bg={bg}>{String(v)}</Badge>;
                },
            },

            { field: "raw_mrp", headerName: "Raw MRP", width: 100, renderCell: (p) => p?.row?.raw_mrp ?? 0 },
            { field: "raw_sale_price", headerName: "Raw Sale", width: 100, renderCell: (p) => p?.row?.raw_sale_price ?? 0 },

            { field: "item_timestamp", headerName: "GF Timestamp", width: 150, renderCell: (p) => p?.row?.item_timestamp ?? "-" },

            { field: "first_seen_at", headerName: "First Seen", width: 175, renderCell: (p) => (p?.row?.first_seen_at ? fmt(p.row.first_seen_at) : "-") },
            { field: "last_seen_at", headerName: "Last Seen", width: 175, renderCell: (p) => (p?.row?.last_seen_at ? fmt(p.row.last_seen_at) : "-") },

            { field: "seen_count", headerName: "Seen", width: 80, renderCell: (p) => p?.row?.seen_count ?? 0 },

            { field: "last_sync_run_id", headerName: "Run ID", width: 90, renderCell: (p) => p?.row?.last_sync_run_id ?? "-" },
            { field: "last_sync_branch_row_id", headerName: "Branch Row", width: 110, renderCell: (p) => p?.row?.last_sync_branch_row_id ?? "-" },
        ],
        []
    );

    const fetchData = async () => {
        setLoading(true);
        try {
            const resp = await syncAuditApi.listUnmatchedItems({
                limit: paginationModel.pageSize,
                page: paginationModel.page,
                branchId: applied.branchId,
                from: applied.from,
                to: applied.to,
                search: applied.search,
            });

            const payload = extractPayload(resp);
            const rawRows = Array.isArray(payload?.rows) ? payload.rows : [];
            const normalized = rawRows.map(normalizeRow);

            setRows(normalized);
            setRowCount(Number(payload?.total || 0));
        } catch (err) {
            console.error(err);
            setRows([]);
            setRowCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [paginationModel.page, paginationModel.pageSize, applied.branchId, applied.from, applied.to, applied.search]);

    // debounce search typing (updates applied.search)
    useEffect(() => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            // do nothing here; only apply when user clicks Apply OR search can auto-apply if you want
        }, 250);
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, [search]);

    const onApply = () => {
        setPaginationModel((p) => ({ ...p, page: 0 }));
        setApplied({
            branchId: branchId ? Number(branchId) : null,
            from: toApiDateTime(fromLocal),
            to: toApiDateTime(toLocal),
            search: search ? String(search) : null,
        });
    };

    const onClear = () => {
        setBranchId("");
        setFromLocal("");
        setToLocal("");
        setSearch("");
        setPaginationModel((p) => ({ ...p, page: 0 }));
        setApplied({
            branchId: null,
            from: null,
            to: null,
            search: null,
        });
    };

    const onExportCsv = async () => {
        try {
            setLoading(true);
            await syncAuditApi.downloadUnmatchedItemsCsv({
                branchId: applied.branchId,
                from: applied.from,
                to: applied.to,
                search: applied.search,
            });
        } catch (err) {
            console.error(err);
            alert(err?.message || "CSV export failed");
        } finally {
            setLoading(false);
        }
    };


    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Unmatched Items</h5>

                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={fetchData}>
                        Refresh
                    </Button>
                    <Button variant="success" onClick={onExportCsv}>
                        Export CSV
                    </Button>
                </div>
            </div>

            {loading && <Loader />}

            <Card className="mb-3">
                <Card.Body>
                    <Row className="align-items-end g-2">
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Branch ID</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={branchId}
                                    onChange={(e) => setBranchId(e.target.value)}
                                    placeholder="eg: 3"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>From (Last Seen)</Form.Label>
                                <Form.Control type="datetime-local" value={fromLocal} onChange={(e) => setFromLocal(e.target.value)} />
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>To (Last Seen)</Form.Label>
                                <Form.Control type="datetime-local" value={toLocal} onChange={(e) => setToLocal(e.target.value)} />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Search (ref code / name)</Form.Label>
                                <Form.Control
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="eg: 10123 or Rice"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12} className="d-flex gap-2 mt-2">
                            <Button variant="primary" onClick={onApply}>
                                Apply
                            </Button>
                            <Button variant="outline-secondary" onClick={onClear}>
                                Clear
                            </Button>

                            <div className="ms-auto text-muted" style={{ fontSize: 12 }}>
                                Showing <b>{rows.length}</b> rows (Total: <b>{rowCount}</b>)
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Box>
                <DataGrid
                    rows={Array.isArray(rows) ? rows : []}
                    columns={columns}
                    getRowId={(r) =>
                        `${r.branch_id || "b"}-${r.item_ref_code || r.gofrugal_item_id || "x"}-${r.last_seen_at || "t"}`
                    }
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    paginationMode="server"
                    rowCount={rowCount}
                    loading={loading}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    disableRowSelectionOnClick
                    getRowHeight={() => "auto"}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            printOptions: { disableToolbarButton: true },
                            csvOptions: { disableToolbarButton: true }, // we use server CSV
                        },
                    }}
                    sx={{ [`& .${gridClasses.cell}`]: { py: 0.5 } }}
                />
            </Box>

            <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Note: Filters are applied on <b>last_seen_at</b> (latest unmatched observations).
            </div>
        </Container>
    );
}
